import { FC, useMemo } from 'react';
import { DomainRow } from './domain-row/domain-row';
import cls from './domains-table.module.scss';
import { useCachedUserInfo, useTranslation } from 'hooks';
import { SkeletonRows } from 'shared/ui/skeleton/skeleton-rows';
import { DomainStatus } from 'store/api/apiTypes';
import { useSubscriptions } from 'hooks/api/useSubscriptions';

interface DomainsTableProps {
  searchQuery?: string;
  onBuyLicenses: () => void;
}

export const DomainsTable: FC<DomainsTableProps> = ({
  searchQuery = '',
  onBuyLicenses,
}) => {
  const { allDomains, isLoading } = useCachedUserInfo();
  const { count } = useSubscriptions();
  const assignAvailable = count?.assigned !== count?.total;
  const { t } = useTranslation();

  const filteredDomains = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? allDomains.filter(domain => domain.domain.toLowerCase().includes(q))
      : allDomains;
    return [...filtered].sort((a, b) => {
      const aNoLicense =
        a.data?.domain_state === DomainStatus.NO_LICENSE ? 1 : 0;
      const bNoLicense =
        b.data?.domain_state === DomainStatus.NO_LICENSE ? 1 : 0;
      return aNoLicense - bNoLicense;
    });
  }, [allDomains, searchQuery]);

  return (
    <table className={cls.domainsTable}>
      <colgroup>
        <col className={cls.urlCol} />
        <col className={cls.statusCol} />
        <col className={cls.actionsCol} />
      </colgroup>
      <thead>
        <tr className={cls.headerRow}>
          <th className={`${cls.headerFirstCell} ${cls.headerCell}`}>
            {t('websiteUrl')}
          </th>
          <th className={cls.headerCell}>{t('status')}</th>
          <th align='right' className={cls.headerCell}></th>
        </tr>
      </thead>
      <tbody>
        {isLoading && filteredDomains.length === 0 ? (
          <SkeletonRows
            count={3}
            colSpan={3}
            cellClassName={cls.loadingCell}
            height={20}
          />
        ) : filteredDomains.length === 0 ? (
          <tr>
            <td colSpan={3} className={cls.emptyCell}>
              {searchQuery.trim()
                ? t('websitesNoSearchResults')
                : t('websitesNoData')}
            </td>
          </tr>
        ) : (
          filteredDomains.map(domain => (
            <DomainRow
              key={domain.domain}
              domain={domain}
              assignAvailable={assignAvailable}
              onBuyLicenses={onBuyLicenses}
            />
          ))
        )}
      </tbody>
    </table>
  );
};
